var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { isKnownSceneType, listKnownSceneTypes, suggestClosest, editDistance, formatExpectedValues, formatInvalidValue, } from '../sceneCatalog';
import { valueKind } from './colorHelpers';
import { issue, toErrorLines, validatePropEnums } from './issues';
import { validateThemePack } from './theme';
var TYPE_ID_RE = /^[a-zA-Z][a-zA-Z0-9]*$/;
/** Lightweight JSON scene schema validation (no external deps). */
export function validateSceneJSON(json, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var strictTypes = Boolean(options.strictTypes);
    var strictProps = options.strictProps !== false;
    var checkTheme = options.validateTheme !== false;
    var extra = new Set((_a = options.extraTypes) !== null && _a !== void 0 ? _a : []);
    var issues = [];
    var knownList = listKnownSceneTypes();
    function typeAllowed(type) {
        return isKnownSceneType(type) || extra.has(type);
    }
    function visit(node, path) {
        var e_1, _a;
        if (node === null || typeof node !== 'object' || Array.isArray(node)) {
            issues.push(issue(path, "expected a scene node object, got ".concat(valueKind(node)), 'expected_object'));
            return;
        }
        var scene = node;
        if (scene.type === undefined || scene.type === null) {
            issues.push(issue("".concat(path, ".type"), "required \u2014 each node needs a type string (".concat(formatExpectedValues(['group', 'rect', 'button', 'lineChart', 'instrumentCluster'], 5), " \u2026)"), 'type_required', ['group', 'button', 'rect', 'lineChart']));
        }
        else if (typeof scene.type !== 'string') {
            issues.push(issue("".concat(path, ".type"), "must be a string, got ".concat(valueKind(scene.type)), 'type_type'));
        }
        else if (scene.type.length === 0) {
            issues.push(issue("".concat(path, ".type"), 'must be a non-empty string', 'type_empty'));
        }
        else if (!TYPE_ID_RE.test(scene.type)) {
            issues.push(issue("".concat(path, ".type"), "invalid identifier \"".concat(scene.type, "\" \u2014 use letters/digits (e.g. \"lineChart\")"), 'type_invalid'));
        }
        else if (!typeAllowed(scene.type)) {
            var suggestions = suggestClosest(scene.type, knownList, 5);
            var nearTypo = suggestions.length > 0 &&
                editDistance(scene.type.toLowerCase(), suggestions[0].toLowerCase()) <= 2;
            if (strictTypes || nearTypo) {
                issues.push(issue("".concat(path, ".type"), formatInvalidValue(scene.type, suggestions.length ? suggestions : knownList.slice(0, 12), {
                    maxShow: suggestions.length ? 5 : 8,
                }), 'type_unknown', suggestions.length ? suggestions : knownList.slice(0, 20)));
            }
        }
        if (scene.props !== undefined) {
            if (scene.props === null || typeof scene.props !== 'object' || Array.isArray(scene.props)) {
                issues.push(issue("".concat(path, ".props"), "must be an object when present, got ".concat(valueKind(scene.props)), 'props_type'));
            }
            else if (strictProps && typeof scene.type === 'string') {
                validatePropEnums(scene.type, scene.props, path, issues);
            }
        }
        if (scene.children !== undefined) {
            if (!Array.isArray(scene.children)) {
                issues.push(issue("".concat(path, ".children"), "must be an array when present, got ".concat(valueKind(scene.children)), 'children_type'));
            }
            else {
                scene.children.forEach(function (child, i) { return visit(child, "".concat(path, ".children[").concat(i, "]")); });
            }
        }
        if (path === 'root' && checkTheme && scene.theme !== undefined) {
            var themeResult = validateThemePack(scene.theme);
            try {
                for (var _b = __values(themeResult.issues), _c = _b.next(); !_c.done; _c = _b.next()) {
                    var th = _c.value;
                    issues.push(__assign(__assign({}, th), { path: th.path.startsWith('theme') ? "root.".concat(th.path) : "root.theme.".concat(th.path) }));
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                }
                finally { if (e_1) throw e_1.error; }
            }
        }
    }
    if (json === undefined) {
        issues.push(issue('root', 'missing scene JSON (got undefined)', 'missing'));
    }
    else {
        visit(json, 'root');
    }
    return {
        valid: issues.length === 0,
        errors: toErrorLines(issues),
        issues: issues,
    };
}
